import { useState, useEffect } from 'react';
import { useGroupCategoryPageService } from '../services/groupCategoryPageService';
import { GroupCategoryPage, PostCategory, LanguageCode } from '../types/post';

export const useGroupCategoryPageManagement = () => {
  const groupCategoryPageService = useGroupCategoryPageService();
  const [groupPages, setGroupPages] = useState<GroupCategoryPage[]>([]);
  const [unassignedCategories, setUnassignedCategories] = useState<PostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGroupPageForm, setShowGroupPageForm] = useState(false);
  const [showGroupPageEditForm, setShowGroupPageEditForm] = useState(false);
  const [editingGroupPage, setEditingGroupPage] = useState<GroupCategoryPage | null>(null);

  const currentLanguage: LanguageCode = 'pl'; // Fixed to Polish as per requirements

  const loadData = async () => {
    setLoading(true);
    try {
      const [groupPagesData, unassignedData] = await Promise.all([
        groupCategoryPageService.getGroupCategoryPages(currentLanguage),
        groupCategoryPageService.getUnassignedCategories(currentLanguage),
      ]);
      setGroupPages(groupPagesData);
      setUnassignedCategories(unassignedData);
    } catch (error) {
      console.error("Error loading group category pages data:", error);
      alert("Nie udało się załadować danych grup kategorii");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentLanguage]);

  const handleCreateGroupPage = async (
    translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
    slug?: string,
    icon?: string,
    color?: string,
    isPublic: boolean = false,
    displayOrder: number = 0
  ) => {
    try {
      await groupCategoryPageService.createGroupCategoryPage(
        translations,
        slug,
        icon,
        color,
        isPublic,
        displayOrder
      );
      setShowGroupPageForm(false);
      loadData(); // Refresh the list
    } catch (error) {
      console.error("Error creating group category page:", error);
      alert("Nie udało się utworzyć grupy kategorii");
    }
  };

  const handleUpdateGroupPage = async (
    groupPageId: string,
    translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
    slug?: string,
    icon?: string,
    color?: string,
    isPublic?: boolean,
    displayOrder?: number
  ) => {
    try {
      await groupCategoryPageService.updateGroupCategoryPage(
        groupPageId,
        translations,
        slug,
        icon,
        color,
        isPublic,
        displayOrder
      );
      setShowGroupPageEditForm(false);
      setEditingGroupPage(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error("Error updating group category page:", error);
      alert("Nie udało się zaktualizować grupy kategorii");
    }
  };

  const handleEditGroupPage = (groupPage: GroupCategoryPage) => {
    setEditingGroupPage(groupPage);
    setShowGroupPageEditForm(true);
  };

  const handleDeleteGroupPage = async (id: string) => {
    if (
      !confirm(
        "Czy na pewno chcesz usunąć tę grupę kategorii? Kategorie zostaną odłączone od grupy, ale nie zostaną usunięte."
      )
    )
      return;
    
    try {
      await groupCategoryPageService.deleteGroupCategoryPage(id);
      loadData(); // Refresh the list
    } catch (error) {
      console.error("Error deleting group category page:", error);
      alert("Nie udało się usunąć grupy kategorii");
    }
  };

  const handleAssignCategoriesToGroup = async (groupPageId: string, categoryIds: string[]) => {
    try {
      await groupCategoryPageService.assignCategoriesToGroupPage(groupPageId, categoryIds);
      loadData(); // Refresh the data
    } catch (error) {
      console.error("Error assigning categories to group:", error);
      alert("Nie udało się przypisać kategorii do grupy");
    }
  };

  const handleRemoveCategoriesFromGroup = async (categoryIds: string[]) => {
    try {
      await groupCategoryPageService.removeCategoriesFromGroupPage(categoryIds);
      loadData(); // Refresh the data
    } catch (error) {
      console.error("Error removing categories from group:", error);
      alert("Nie udało się usunąć kategorii z grupy");
    }
  };

  const openNewGroupPageForm = () => {
    setShowGroupPageForm(true);
  };

  const closeGroupPageForm = () => {
    setShowGroupPageForm(false);
  };

  const closeGroupPageEditForm = () => {
    setShowGroupPageEditForm(false);
    setEditingGroupPage(null);
  };

  return {
    // Data
    groupPages,
    unassignedCategories,
    loading,
    currentLanguage,
    
    // UI State
    showGroupPageForm,
    showGroupPageEditForm,
    editingGroupPage,
    
    // Actions
    loadData,
    handleCreateGroupPage,
    handleUpdateGroupPage,
    handleEditGroupPage,
    handleDeleteGroupPage,
    handleAssignCategoriesToGroup,
    handleRemoveCategoriesFromGroup,
    openNewGroupPageForm,
    closeGroupPageForm,
    closeGroupPageEditForm,
  };
};
